import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  AlertTriangle, Heart, Phone, User, Droplet, Pill, 
  Hospital, Shield, Calendar, Clock
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmergencyViewPage = () => {
  const { publicId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchEmergencyProfile();
  }, [publicId]);

  const fetchEmergencyProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/emergency/${publicId}`);
      setProfile(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Emergency profile not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age} yrs`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-red-700 font-semibold">Loading Emergency Info...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-300">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-red-700 mb-2">Profile Not Found</h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-600 to-red-800">
      {/* Emergency Header */}
      <div className="bg-red-700 text-white p-4 text-center sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-center gap-2">
          <AlertTriangle className="h-6 w-6 animate-pulse" />
          <h1 className="text-xl font-bold">EMERGENCY MEDICAL INFO</h1>
          <AlertTriangle className="h-6 w-6 animate-pulse" />
        </div>
        <p className="text-red-200 text-sm mt-1">Emergency Medical Card</p>
      </div>

      <div className="p-4 max-w-lg mx-auto space-y-4 pb-8">
        {/* Patient Identity */}
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="bg-red-600 text-white p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-full">
                <User className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{profile.full_name}</h2>
                <div className="flex items-center gap-3 text-red-100 text-sm mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {calculateAge(profile.date_of_birth)}
                  </span>
                  {profile.gender && (
                    <span className="capitalize">{profile.gender}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Blood Group - Prominent */}
          <div className="flex">
            <div className="flex-1 bg-red-100 p-4 text-center border-r">
              <Droplet className="h-8 w-8 text-red-600 mx-auto mb-1" />
              <p className="text-3xl font-bold text-red-700">{profile.blood_group}</p>
              <p className="text-xs text-red-600">Blood Group</p>
            </div>
            {profile.organ_donor && (
              <div className="flex-1 bg-green-100 p-4 text-center">
                <Heart className="h-8 w-8 text-green-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-green-700">DONOR</p>
                <p className="text-xs text-green-600">Organ Donor</p>
              </div>
            )}
          </div>
        </Card>

        {/* Allergies - Critical with Severity */}
        {profile.allergies && profile.allergies.length > 0 && (
          <Card className="border-2 border-red-400 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-bold text-red-700">ALLERGIES</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.allergies.map((allergy, index) => {
                  // Handle both old format (string) and new format ({name, severity})
                  const isObject = typeof allergy === 'object';
                  const name = isObject ? allergy.name : allergy;
                  const severity = isObject ? allergy.severity : 'severe';
                  
                  const severityColors = {
                    severe: 'bg-red-600',
                    moderate: 'bg-orange-500',
                    mild: 'bg-yellow-500'
                  };
                  
                  return (
                    <Badge 
                      key={index} 
                      className={`${severityColors[severity] || 'bg-red-600'} text-white text-sm px-3 py-1`}
                    >
                      {severity === 'severe' && '⚠️ '}
                      {name}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Critical Conditions */}
        {profile.critical_conditions && profile.critical_conditions.length > 0 && (
          <Card className="border-orange-300 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-6 w-6 text-orange-600" />
                <h3 className="text-lg font-bold text-orange-700">Medical Conditions</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.critical_conditions.map((condition, index) => (
                  <Badge key={index} className="bg-orange-500 text-white text-sm px-3 py-1">
                    {condition}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Medications */}
        {profile.current_medications && profile.current_medications.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Pill className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-bold text-blue-700">Current Medications</h3>
              </div>
              <div className="space-y-2">
                {profile.current_medications.map((med, index) => (
                  <div key={index} className="flex items-center gap-2 bg-blue-50 p-2 rounded">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-700">{med}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contacts */}
        {profile.emergency_contacts && profile.emergency_contacts.length > 0 && (
          <Card className="border-green-300">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-bold text-green-700">Emergency Contacts</h3>
              </div>
              <div className="space-y-3">
                {profile.emergency_contacts.map((contact, index) => (
                  <a 
                    key={index}
                    href={`tel:${contact.phone}`}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-slate-800">
                        {contact.name}
                        {contact.is_primary && (
                          <Badge className="ml-2 bg-green-600 text-xs">Primary</Badge>
                        )}
                      </p>
                      <p className="text-sm text-slate-500 capitalize">{contact.relationship}</p>
                    </div>
                    <div className="flex items-center gap-2 text-green-700 font-semibold">
                      <Phone className="h-5 w-5" />
                      {contact.phone}
                    </div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Doctor / Hospital */}
        {(profile.doctor_name || profile.preferred_hospital) && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hospital className="h-6 w-6 text-purple-600" />
                <h3 className="text-lg font-bold text-purple-700">Medical Provider</h3>
              </div>
              
              {profile.preferred_hospital && (
                <div className="mb-3">
                  <p className="text-sm text-slate-500">Preferred Hospital</p>
                  <p className="font-semibold text-slate-800">{profile.preferred_hospital}</p>
                </div>
              )}
              
              {profile.doctor_name && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Doctor</p>
                    <p className="font-semibold text-slate-800">{profile.doctor_name}</p>
                  </div>
                  {profile.doctor_phone && (
                    <a 
                      href={`tel:${profile.doctor_phone}`}
                      className="flex items-center gap-1 text-purple-600 font-semibold"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Special Instructions */}
        {profile.special_instructions && (
          <Card className="border-purple-300 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-purple-700">Special Instructions</h3>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{profile.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center pt-4">
          <p className="text-white/80 text-sm">
            Last updated: {formatDate(profile.last_updated)}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2 text-white">
            <Shield className="h-4 w-4" />
            <span className="text-sm">Secured by Emergency Card</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyViewPage;
